import { Box, Button } from "@mui/material";
import useColors from "../../api/hooks/useColors";
import SvgToIcon from "../../icons/SvgToIcon";
import LoadingIcon from "../LoadingIcon";

export default function OutlinedButton({
  onClick,
  color = "inherit",
  size = "small",
  icon = null,
  disabledIcon = null,
  disabled = false,
  loading = false,
  children = null,
  iconSize = null,
  sx = {},
  ...props
}) {
  /*
  If you use a currentColor .svg, the enabled/disabled colors will be the same as the text color.
  If you use a colored .svg (for example, a company's logo like Jira), you need to provide a disabledIcon .svg.
  If you provide only an icon (no text), the button will be square.
  */
  const currentColorMode = !disabledIcon;
  const colors = useColors();

  const colorMap = {
    inherit: {
      enabled: {
        text: colors.button_outlined_inherit_enabled_text,
        bg: colors.button_outlined_inherit_enabled_bg,
        stroke: colors.button_outlined_inherit_enabled_stroke,
        icon: colors.button_outlined_inherit_enabled_icon,
      },
      hover: {
        text: colors.button_outlined_inherit_hovered_text,
        bg: colors.button_outlined_inherit_hovered_bg,
        stroke: colors.button_outlined_inherit_hovered_stroke,
        icon: colors.button_outlined_inherit_hovered_icon,
      },
      disabled: {
        text: colors.button_outlined_disabled_text,
        bg: colors.button_outlined_disabled_bg,
        stroke: colors.button_outlined_disabled_stroke,
        icon: colors.button_outlined_disabled_icon,
      },
    },
  };

  const sizeMap = {
    xs: {
      fontSize: "12px",
      fontWeight: 400,
      height: "24px",
      minWidth: "44px",
      paddingX: "10px",
      borderRadius: "8px",
      loadingSize: 10,
      iconSize: 14,
    },
    small: {
      fontSize: "12px",
      fontWeight: 500,
      height: "32px",
      minWidth: "32px",
      paddingX: "10px",
      borderRadius: "8px",
      loadingSize: 14,
      iconSize: 18,
    },
    medium: {
      fontSize: "14px",
      fontWeight: 500,
      height: "36px",
      minWidth: "64px",
      paddingX: "10px",
      borderRadius: "12px",
      loadingSize: 16,
      iconSize: 20,
    },
    large: {
      fontSize: "14px",
      fontWeight: 500,
      height: "48px",
      minWidth: "64px",
      paddingX: "10px",
      borderRadius: "14px",
      loadingSize: 18,
      iconSize: 20,
    },
  };

  const colorConfig = colorMap[color];
  const sizeConfig = sizeMap[size];
  const width = !children && icon ? sizeConfig.height : "fit-content";

  return (
    <Button
      variant="text"
      onClick={onClick}
      disabled={disabled || loading}
      disableRipple
      disableFocusRipple
      disableElevation
      sx={{
        position: "relative",
        textTransform: "none",
        fontSize: sizeConfig.fontSize,
        fontWeight: sizeConfig.fontWeight,
        height: sizeConfig.height,
        paddingX: sizeConfig.paddingX,
        width: width,
        minWidth: children ? sizeConfig.minWidth : width,
        borderRadius: sizeConfig.borderRadius,
        color: colorConfig.enabled.text,
        backgroundColor: colorConfig.enabled.bg,
        border: "1px solid " + colorConfig.enabled.stroke,
        "&:hover": {
          backgroundColor: colorConfig.hover.bg,
          color: colorConfig.hover.text,
          border: "1px solid " + colorConfig.hover.stroke,
        },
        "&:disabled": {
          backgroundColor: colorConfig.disabled.bg,
          color: colorConfig.disabled.text,
          border: "1px solid " + colorConfig.disabled.stroke,
        },
        ...sx,
      }}
      {...props}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={0.7}
        width="100%"
        justifyContent="center"
        whiteSpace="nowrap"
        style={{ visibility: loading ? "hidden" : "visible" }}
      >
        {icon && (
          <SvgToIcon
            IconLogo={currentColorMode ? icon : !disabled ? icon : disabledIcon}
            width={iconSize || sizeConfig.iconSize}
            height={iconSize || sizeConfig.iconSize}
            style={
              currentColorMode
                ? {
                    color: !disabled
                      ? colorConfig.enabled.icon
                      : colorConfig.disabled.icon,
                  }
                : {}
            }
          />
        )}
        {children && children}
      </Box>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadingIcon type="circular" size={sizeConfig.loadingSize} />
        </Box>
      )}
    </Button>
  );
}
